<template>
  <main class="webx-callback-page">
    <section class="webx-callback-card" aria-live="polite">
      <h1>{{ t('auth.webxLoginTitle') }}</h1>
      <template v-if="loading">
        <van-loading size="28" color="#315f4b" />
        <p>{{ t('auth.webxLoginConfirming') }}</p>
      </template>
      <template v-else>
        <p class="webx-callback-error">{{ errorMessage }}</p>
        <AuthThemeButton block @click="retryBinding">
          {{ t('auth.webxRetry') }}
        </AuthThemeButton>
        <AuthThemeButton block variant="cancel" class="webx-skip-btn" @click="goHome">
          {{ t('auth.webxSkip') }}
        </AuthThemeButton>
      </template>
    </section>
  </main>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { startWebxBinding, webxComplete } from '@/api/auth';
import { getWebxErrorMessage } from '@/utils/webx-auth';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const loading = ref(true);
const errorMessage = ref('');

async function goHome() {
  await router.replace('/home/index');
}

async function retryBinding() {
  loading.value = true;
  errorMessage.value = '';
  try {
    await startWebxBinding();
  } catch (error) {
    loading.value = false;
    errorMessage.value = error?.message || t('auth.webxLoginFailed');
  }
}

onMounted(async () => {
  const ticket = String(route.query.ticket || '').trim();
  const callbackError = String(route.query.error || '').trim();
  window.history.replaceState(window.history.state, '', route.path);

  if (callbackError || !ticket) {
    loading.value = false;
    errorMessage.value = getWebxErrorMessage(callbackError || 'ticket_missing', t);
    return;
  }

  try {
    const result = await webxComplete({ ticket });
    if (result?.mode === 'bind' && result?.bound === true) {
      await goHome();
      return;
    }
    throw new Error(t('auth.webxLoginFailed'));
  } catch (error) {
    loading.value = false;
    const errorCode = String(error?.payload?.error || '').trim();
    errorMessage.value = errorCode
      ? getWebxErrorMessage(errorCode, t)
      : (error?.message || t('auth.webxLoginFailed'));
  }
});
</script>

<style scoped lang="scss">
.webx-callback-page {
  display: flex;
  min-height: var(--app-viewport-height);
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  background: #f7f5ef;
}

.webx-callback-card {
  width: 100%;
  max-width: 420px;
  padding: 44px 32px;
  border-radius: 24px;
  background: #ffffff;
  text-align: center;

  h1 {
    margin: 0 0 30px;
    color: #1e2c27;
    font-size: 26px;
    line-height: 36px;
  }

  p {
    margin: 18px 0 0;
    color: #48544f;
    font-size: 16px;
    line-height: 24px;
  }

  .auth-theme-btn {
    margin-top: 28px;
    background: #315f4b;
  }

  .webx-skip-btn {
    margin-top: 12px;
    background: #f0f0f0;
    color: #333333;
    box-shadow: none;
  }
}

.webx-callback-error {
  color: #d93025 !important;
}
</style>
